import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Trash2, Plus, CheckCircle, Link2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
    adminInvoices,
    adminCreateInvoice,
    adminSendInvoice,
    adminInvoicePayLink,
    adminMarkInvoicePaid,
    adminDeleteInvoice,
    adminLinkInvoice,
    adminClients,
} from "../../lib/api";

const STATUS_STYLES = {
    draft: "bg-ink/5 text-ink/60",
    sent: "bg-worx/10 text-worx",
    paid: "bg-green-100 text-green-800",
};

export const Invoices = ({ token }) => {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("manual");
    const [form, setForm] = useState({ client_name: "", client_email: "", description: "", hours: "", rate: "", notes: "" });
    const [busy, setBusy] = useState(false);

    const load = () => adminInvoices(token).then(setItems).catch(() => {});
    useEffect(() => {
        load();
        adminClients(token).then(setClients).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const linked = clientId !== "manual" ? clients.find((c) => c.client_id === clientId) : null;
    const total = (parseFloat(form.hours) || 0) * (parseFloat(form.rate) || 0);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await adminCreateInvoice(token, {
                ...form,
                client_id: linked ? linked.client_id : "",
                hours: parseFloat(form.hours),
                rate: parseFloat(form.rate),
            });
            toast.success(linked ? `Invoice created for ${linked.name}.` : "Invoice created.");
            setForm({ client_name: "", client_email: "", description: "", hours: "", rate: "", notes: "" });
            setClientId("manual");
            load();
        } catch {
            toast.error("Could not create invoice.");
        } finally {
            setBusy(false);
        }
    };

    const send = async (inv) => {
        if (!window.confirm(`Email invoice ${inv.invoice_no} ($${inv.amount.toFixed(2)}) to ${inv.client_email}?`)) return;
        try {
            const r = await adminSendInvoice(token, inv.invoice_id);
            toast.success(r.pay_url ? "Invoice sent with card payment link." : "Invoice sent (no card link — Stripe unavailable).");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed.");
        }
    };

    const payLink = async (inv) => {
        try {
            let link = inv.pay_link;
            if (!link) {
                const r = await adminInvoicePayLink(token, inv.invoice_id);
                link = r.pay_link;
            }
            await navigator.clipboard.writeText(link);
            toast.success("Secure payment link copied — text or email it to your client.");
            load();
        } catch {
            toast.error("Could not create payment link.");
        }
    };

    const markPaid = async (inv) => {
        await adminMarkInvoicePaid(token, inv.invoice_id).catch(() => {});
        toast.success(`${inv.invoice_no} marked paid.`);
        load();
    };

    const remove = async (inv) => {
        if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
        await adminDeleteInvoice(token, inv.invoice_id).catch(() => {});
        load();
    };

    const link = async (inv, cid) => {
        if (!cid) return;
        try {
            await adminLinkInvoice(token, inv.invoice_id, cid);
            toast.success("Invoice linked to client.");
            load();
        } catch {
            toast.error("Could not link invoice.");
        }
    };

    return (
        <section className="border border-ink/10 bg-white" data-testid="admin-invoices-panel">
            <div className="px-6 py-4 border-b border-ink/10">
                <h2 className="font-display font-medium text-xl">Invoices</h2>
                <p className="text-xs text-ink/50 mt-1">
                    Time-based invoices — hours × rate, emailed to the client with a secure card payment link.
                </p>
            </div>
            <form onSubmit={create} data-testid="invoice-form" className="p-6 space-y-4">
                <div>
                    <Label>Link a client</Label>
                    <Select value={clientId} onValueChange={setClientId}>
                        <SelectTrigger data-testid="invoice-client-select" className="mt-1.5 rounded-none">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="manual" data-testid="invoice-client-manual">Enter manually…</SelectItem>
                            {clients.map((c) => (
                                <SelectItem key={c.client_id} value={c.client_id} data-testid={`invoice-client-opt-${c.client_id.slice(0, 8)}`}>
                                    {c.name}{c.company ? ` — ${c.company}` : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {linked ? (
                    <p className="text-sm text-ink/70 border border-ink/10 bg-paper px-3.5 py-2.5 flex items-center justify-between" data-testid="invoice-linked-client">
                        <span>
                            Linked to <strong>{linked.name}</strong>
                            {linked.email ? ` · ${linked.email}` : " · no email on file — add one before sending"}
                        </span>
                        <button type="button" onClick={() => setClientId("manual")} className="text-ink/40 hover:text-worx" title="Unlink">
                            <X size={14} />
                        </button>
                    </p>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="i-client">Client name *</Label>
                            <Input id="i-client" data-testid="invoice-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required={!linked} className="mt-1.5 rounded-none" />
                        </div>
                        <div>
                            <Label htmlFor="i-email">Client email *</Label>
                            <Input id="i-email" data-testid="invoice-email-input" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} required={!linked} className="mt-1.5 rounded-none" />
                        </div>
                    </div>
                )}
                <div>
                    <Label htmlFor="i-desc">Work description *</Label>
                    <Input id="i-desc" data-testid="invoice-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Website development — milestone 1" className="mt-1.5 rounded-none" />
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                    <div>
                        <Label htmlFor="i-hours">Hours *</Label>
                        <Input id="i-hours" data-testid="invoice-hours-input" type="number" min="0.25" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label htmlFor="i-rate">Rate $/hr *</Label>
                        <Input id="i-rate" data-testid="invoice-rate-input" type="number" min="1" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} required className="mt-1.5 rounded-none" />
                    </div>
                    <div className="pb-0.5">
                        <p className="text-xs uppercase tracking-[0.2em] text-ink/50">Total</p>
                        <p className="font-display font-semibold text-2xl" data-testid="invoice-total">${total.toFixed(2)}</p>
                    </div>
                </div>
                <div>
                    <Label htmlFor="i-notes">Notes (shown on invoice email)</Label>
                    <Textarea id="i-notes" data-testid="invoice-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5 rounded-none" />
                </div>
                <Button data-testid="invoice-create-btn" type="submit" disabled={busy} className="bg-ink hover:bg-worx text-paper rounded-none">
                    {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus className="mr-2" size={16} />}
                    Create invoice
                </Button>
            </form>
            {items.length > 0 && (
                <ul data-testid="invoices-list" className="divide-y divide-ink/10 border-t border-ink/10">
                    {items.map((inv) => (
                        <li key={inv.invoice_id} className="px-6 py-3.5 flex items-center justify-between gap-3" data-testid={`invoice-${inv.invoice_no}`}>
                            <div className="min-w-0">
                                <p className="font-medium text-sm">
                                    {inv.invoice_no} · ${inv.amount.toFixed(2)}{" "}
                                    <span className={`ml-1 text-xs px-2 py-0.5 ${STATUS_STYLES[inv.status] || ""}`} data-testid={`invoice-status-${inv.invoice_no}`}>
                                        {inv.status}
                                    </span>
                                </p>
                                <p className="text-xs text-ink/50 truncate">
                                    {inv.client_name} · {inv.description} · {inv.hours} hrs × ${inv.rate}/hr
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {!inv.client_id && clients.length > 0 && (
                                    <select
                                        data-testid={`invoice-link-${inv.invoice_no}`}
                                        defaultValue=""
                                        onChange={(e) => link(inv, e.target.value)}
                                        className="text-xs border border-worx/40 text-worx px-2 py-1.5 bg-white cursor-pointer"
                                        title="Link this invoice to a client"
                                    >
                                        <option value="" disabled>Link client…</option>
                                        {clients.map((c) => (
                                            <option key={c.client_id} value={c.client_id}>{c.name}</option>
                                        ))}
                                    </select>
                                )}
                                {inv.status !== "paid" && (
                                    <button data-testid={`invoice-paylink-${inv.invoice_no}`} onClick={() => payLink(inv)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors" title="Copy secure card payment link">
                                        <Link2 size={13} /> {inv.pay_link ? "Copy link" : "Get link"}
                                    </button>
                                )}
                                {inv.status === "draft" && (
                                    <button data-testid={`invoice-send-${inv.invoice_no}`} onClick={() => send(inv)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors" title="Email to client">
                                        <Send size={13} /> Send
                                    </button>
                                )}
                                {inv.status !== "paid" && (
                                    <button data-testid={`invoice-paid-${inv.invoice_no}`} onClick={() => markPaid(inv)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-green-600 hover:text-green-700 transition-colors" title="Mark paid">
                                        <CheckCircle size={13} /> Paid
                                    </button>
                                )}
                                <button data-testid={`invoice-delete-${inv.invoice_no}`} onClick={() => remove(inv)} className="text-ink/40 hover:text-worx transition-colors p-1.5" title="Delete">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
